import { Select } from "antd";
import React, { useEffect, useState, useCallback } from "react";
import { FaMinus, FaPlus } from "react-icons/fa6";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { api, API_BASE_URL } from "../../../config/api";
import { toast } from "react-toastify";
import AsyncSelect from 'react-select/async';
import { CANADIAN_PROVINCES, BRAZILIAN_STATES } from '../../../utils/states';
import EstimatePopup from "./EstimatePopup";
import { handleCalculate, validatePopupFormData } from "./estimateCalculations";

const tempData = [
    {
        label: "IOR",
        items: [
            "IOR",
            "Export documentation",
            "Customs clearance",
            "Handling",
            "Collection",
            "Airfreight",
            "Administration",
            "Duties and taxes",
            "Compliance",
            "VAT",
            "Final delivery",
            "Destination charges",
            "Leiability coverage",
            "Approvals",
            "Import permit",
            "Bank fees",
            "Validation",
            "THC",
        ],
    },
    {
        label: "EOR",
        items: [
            "EOR",
            "Export clearance",
            "Export documentation",
            "Collection",
            "Airfreight",
            "Handling",
            "Administration",
            "Bank fees",
            "Export compliance",
        ],
    },
    {
        label: "EOR & IOR",
        items: [
            "EOR",
            "Export documentation",
            "IOR",
            "Customs clearance",
            "Handling",
            "Collection",
            "Airfreight",
            "Administration",
            "Duties and taxes",
            "Compliance",
            "VAT",
            "Final delivery",
            "Destination charges",
            "Leiability coverage",
            "Approvals",
            "Import permit",
            "Bank fees",
            "Validation",
            "THC",
        ],
    },
];

const ServiceTemp = ({
    formData,
    setFormData,
    validationErrors,
    setValidationErrors,
    templateSelected,
    attemptedSubmit,
    setTemplateSelected,
    isPopupOpen,
    setIsPopupOpen,
    areMandatoryFieldsFilled,
    areCurrenciesAligned
}) => {
    const [popupFormData, setPopupFormData] = useState({
        items: [{
            itemName: "",
            quantity: "",
            itemValue: "",
            countryOrigin: "",
            itemHSCode: "",
            hsCodeSource: "database" // Add this new field
        }],
        insuranceSelected: "NO",
        requiredDoc: "not_Required",
        eccnCode: "",
    });
    const [adminTemplate, setAdminTemplate] = useState(null);
    const [eccnCodes, setEccnCodes] = useState([]);
    const [totalItemsValue, setTotalItemsValue] = useState(0);
    const [totalItemsQuantity, setTotalItemsQuantity] = useState(0); // Sum of dimension quantities
    const [itemsTotalQty, setItemsTotalQty] = useState(0); // Sum of item quantities
    const [totalDimensionsWeight, setTotalDimensionsWeight] = useState(0);
    const [totalDimensionsLength, setTotalDimensionsLength] = useState(0);
    const [totalDimensionsWidth, setTotalDimensionsWidth] = useState(0);
    const [totalDimensionsHeight, setTotalDimensionsHeight] = useState(0);
    const [insurance, setInsurance] = useState(0);
    const [airfreight, setAirfreight] = useState(0);
    const [cif, setCif] = useState(0);
    const [dhlEstimation, setDhlEstimation] = useState(null);
    const [duty, setDuty] = useState(0);
    const [collectionValue, setCollectionValue] = useState(0);
    const [accessToken, setAccessToken] = useState(null);
    const [tokenExpiry, setTokenExpiry] = useState(null);
    const [senderState, setSenderState] = useState('');
    const [receiverState, setReceiverState] = useState('');
    const [popupValidationErrors, setPopupValidationErrors] = useState({});
    const [isCalculating, setIsCalculating] = useState(false);
    const [disabledCountries, setDisabledCountries] = useState([]);
    const [disabledDutyVatCountries, setDisabledDutyVatCountries] = useState([]);

    // Unit conversion functions (from EstimateForm)
    const convertLbsToKg = (lbs) => {
        return lbs * 0.453592;
    };

    const convertInchesToCm = (inches) => {
        return inches * 2.54;
    };

    // Helper function to get converted weight in kg
    const getWeightInKg = (weight) => {
        if (!weight || !weight.value) return 0;
        const value = parseFloat(weight.value);
        return weight.unit === 'lbs' ? convertLbsToKg(value) : value;
    };

    // Helper function to get converted dimension in cm
    const getDimensionInCm = (dimension) => {
        if (!dimension || !dimension.value) return 0;
        const value = parseFloat(dimension.value);
        return dimension.unit === 'in' ? convertInchesToCm(value) : value;
    };

    useEffect(() => {
        const fetchDisabledCountries = async () => {
            try {
                const response = await api.get(`${API_BASE_URL}/countries/disabled`);
                setDisabledCountries(response.data);
            } catch (error) {
                console.error('Error fetching disabled countries:', error);
            }
        };
        const fetchDisabledDutyVatCountries = async () => {
            try {
                const response = await api.get(`${API_BASE_URL}/duty-vat/disabled`);
                setDisabledDutyVatCountries(response.data);
            } catch (error) {
                console.error('Error fetching disabled duty & VAT countries:', error);
            }
        };
        const fetchEccnCodes = async () => {
            try {
                const response = await api.get(`${API_BASE_URL}/eccn`);
                setEccnCodes(response.data);
            } catch (error) {
                console.error("Error fetching ECCN codes:", error);
                toast.error("Failed to load ECCN codes");
            }
        };
        fetchDisabledCountries();
        fetchDisabledDutyVatCountries();
        fetchEccnCodes();
    }, []);

    useEffect(() => {
        if (!formData.selectedTemplate) {
            setTemplateSelected(false);
        } else {
            setTemplateSelected(true);
        }
    }, [formData.selectedTemplate, setTemplateSelected]);

    useEffect(() => {
        if (
            formData.loadCountry &&
            formData.unloadCountry &&
            formData.selectedTemplate &&
            formData.currency &&
            formData.pcsPackage.length > 0
        ) {
            // Always pass "kg" as unit regardless of the form weight unit
            fetchAdminTemplate(
                formData.loadCountry,
                formData.unloadCountry,
                formData.selectedTemplate,
                formData.currency,
                "kg"  // Always pass "kg" instead of formData.pcsPackage[0].weight.unit
            );
        }
    }, [
        formData.loadCountry,
        formData.unloadCountry,
        formData.selectedTemplate,
        formData.currency,
        formData.pcsPackage,
    ]);

    useEffect(() => {
        const calculateTotalValue = () => {
            const total = popupFormData.items.reduce((sum, item) => {
                const value = parseFloat(item.itemValue) || 0;
                const quantity = parseFloat(item.quantity) || 0;
                return sum + (value * quantity);
            }, 0);
            const totalQty = popupFormData.items.reduce((sum, item) => {
                return sum + (parseFloat(item.quantity) || 0);
            }, 0);
            setTotalItemsValue(total);
            setItemsTotalQty(totalQty);
        };
        calculateTotalValue();
    }, [popupFormData.items]);

    useEffect(() => {
        const calculateTotalDimensions = () => {
            // Use conversion functions for accurate calculations
            const totalWeight = formData.pcsPackage?.reduce((sum, pkg) => {
                const weightInKg = getWeightInKg(pkg.weight);
                const quantity = parseFloat(pkg.quantity) || 0;
                return sum + (weightInKg * quantity);
            }, 0) || 0;

            const totalLength = formData.pcsPackage?.reduce((sum, pkg) => {
                const lengthInCm = getDimensionInCm(pkg.length);
                const quantity = parseFloat(pkg.quantity) || 0;
                return sum + (lengthInCm * quantity);
            }, 0) || 0;

            const totalWidth = formData.pcsPackage?.reduce((sum, pkg) => {
                const widthInCm = getDimensionInCm(pkg.width);
                const quantity = parseFloat(pkg.quantity) || 0;
                return sum + (widthInCm * quantity);
            }, 0) || 0;

            const totalHeight = formData.pcsPackage?.reduce((sum, pkg) => {
                const heightInCm = getDimensionInCm(pkg.height);
                const quantity = parseFloat(pkg.quantity) || 0;
                return sum + (heightInCm * quantity);
            }, 0) || 0;

            const totalQty = formData.pcsPackage?.reduce((sum, pkg) => {
                return sum + (parseFloat(pkg.quantity) || 0);
            }, 0) || 0;

            setTotalDimensionsWeight(totalWeight);
            setTotalDimensionsLength(totalLength);
            setTotalDimensionsWidth(totalWidth);
            setTotalDimensionsHeight(totalHeight);
            setTotalItemsQuantity(totalQty);
        };
        calculateTotalDimensions();
    }, [formData.pcsPackage]);

    useEffect(() => {
        const calculateInsurance = () => {
            const calculatedInsurance = popupFormData.insuranceSelected === "YES" ? totalItemsValue * 0.0075 : 0;
            setInsurance(calculatedInsurance);
        };
        calculateInsurance();
    }, [popupFormData.insuranceSelected, totalItemsValue]);

    useEffect(() => {
        const calculateAirfreightCost = () => {
            // Use converted dimensions in CM for volumetric weight calculation
            const totalVolumetricWeight = formData.pcsPackage?.reduce((sum, pkg) => {
                const lengthCm = getDimensionInCm(pkg.length);
                const widthCm = getDimensionInCm(pkg.width);
                const heightCm = getDimensionInCm(pkg.height);
                const quantity = parseFloat(pkg.quantity) || 0;

                let volWeight = (lengthCm * widthCm * heightCm) / 6000;
                return sum + (volWeight * quantity);
            }, 0) || 0;

            // Take the maximum between volumetric weight and actual weight (both in kg)
            const chargeableWeight = Math.max(totalVolumetricWeight, totalDimensionsWeight);

            let airfreightCost;
            if (chargeableWeight < 360) {
                airfreightCost = 6.50 * chargeableWeight + 250;
            } else if (chargeableWeight >= 360 && chargeableWeight <= 1080) {
                airfreightCost = 5.00 * chargeableWeight + 250;
            } else {
                airfreightCost = 4.00 * chargeableWeight + 250;
            }
            setAirfreight(airfreightCost);
        };
        calculateAirfreightCost();
    }, [formData.pcsPackage, totalDimensionsWeight]);

    useEffect(() => {
        if (formData) {
            if (needsStateSelection(formData.loadCountry) && !senderState) {
                setSenderState(formData.loadState || formData.loadCity || '');
            }
            if (needsStateSelection(formData.unloadCountry) && !receiverState) {
                setReceiverState(formData.unloadState || formData.unloadCity || '');
            }
        }
    }, [formData.loadCountry, formData.unloadCountry, formData.loadCity, formData.unloadCity]);

    const fetchAdminTemplate = async (loadCountry, unloadCountry, selectedTemplate, currency, unit) => {
        try {
            // console.log(`Fetching admin template: ${loadCountry} to ${unloadCountry}, template: ${selectedTemplate}`);

            // Always use "kg" for the API call regardless of the passed parameter
            const standardizedWeightUnit = "kg";

            // Use the new dedicated /admin-template endpoint
            const response = await api.get(`${API_BASE_URL}/estimates/admin-template`, {
                params: {
                    loadCountry,
                    unloadCountry,
                    selectedTemplate,
                    airfreightCurrency: currency,
                    afcWeightUnit: standardizedWeightUnit // Always use "kg"
                },
                timeout: 12000, // 12 second timeout - reasonable for the optimized query
            });

            const estimateData = response.data;
            // console.log(`Received ${estimateData.length} estimates from admin-template API`);

            if (estimateData && estimateData.length > 0) {
                // Find exact match using the same logic as your existing code
                const exactMatch = estimateData.find((estimate) => {
                    const loadCountryMatch = Array.isArray(estimate.loadCountry)
                        ? estimate.loadCountry.includes(loadCountry)
                        : estimate.loadCountry === loadCountry;

                    return (
                        loadCountryMatch &&
                        estimate.unloadCountry === unloadCountry &&
                        estimate.selectedTemplate.name === selectedTemplate &&
                        estimate.airfreightCurrency === currency &&
                        estimate.afcWeightUnit === standardizedWeightUnit // Use standardized unit
                    );
                });

                if (exactMatch) {
                    // console.log(`Found exact match: estimateId ${exactMatch.estimateId}`);
                    setAdminTemplate(exactMatch);
                } else {
                    // console.log("No exact match found in returned estimates");
                    setAdminTemplate(null);
                }
            } else {
                // console.log("No estimates returned from admin-template API");
                setAdminTemplate(null);
            }
        } catch (error) {
            console.error("Error fetching admin template:", error);

            // Enhanced error handling
            if (error.code === 'ECONNABORTED' || error.message.includes('timeout')) {
                console.error("Request timeout - admin template API took too long");
                toast.error("Request timeout. Please try again or contact support if the issue persists.");
            } else if (error.response) {
                // Server responded with error status
                if (error.response.status === 408) {
                    console.error("Server timeout for admin template query");
                    toast.error("Server timeout. The database query took too long. Please try again.");
                } else if (error.response.status === 400) {
                    console.error("Bad request - missing parameters");
                    toast.error("Invalid request parameters. Please check your selection and try again.");
                } else if (error.response.status === 404) {
                    // console.log("No matching admin template found");
                    // Don't show error for 404, just set null
                } else {
                    console.error(`Server error: ${error.response.status}`);
                    toast.error(`Server error: ${error.response.status}. Please try again.`);
                }
            } else if (error.request) {
                // Network error
                console.error("Network error during admin template fetch");
                toast.error("Network error. Please check your connection and try again.");
            } else {
                // Other errors
                console.error("Unexpected error:", error.message);
                toast.error("Failed to load admin template. Please try again.");
            }

            setAdminTemplate(null);
        }
    };

    const getValidationErrorClass = (index, field) => {
        return validationErrors[`service_${index}_${field}`]
            ? "border-red-500"
            : "active:border-cus-blue focus:border-cus-blue";
    };

    const validateField = (value, field) => {
        if (field === "numberOfItem") {
            return value.trim() !== "" && !isNaN(value) && parseFloat(value) > 0;
        }
        if (field === "description") {
            return value.trim() !== "";
        }
        return true;
    };

    const handleServiceInputChange = (index, field, e) => {
        const { value } = e.target;
        let sanitizedValue = value;

        if (field === "numberOfItem") {
            sanitizedValue = value.replace(/[^0-9.]/g, "");
            const parts = sanitizedValue.split(".");
            if (parts.length > 2) parts.pop();
            sanitizedValue = parts.join(".");
            if (parseFloat(sanitizedValue) <= 0 || isNaN(parseFloat(sanitizedValue))) sanitizedValue = "";
        } else if (field === "exclVAT") {
            sanitizedValue = value.replace(/[^0-9.]/g, "");
            const parts = sanitizedValue.split(".");
            if (parts.length > 2) parts.pop();
            sanitizedValue = parts.join(".");
        }

        const isValid = validateField(sanitizedValue, field);
        const updatedServices = [...formData.services];
        updatedServices[index][field] = sanitizedValue;

        setFormData({ ...formData, services: updatedServices });

        if (field !== "exclVAT") {
            setValidationErrors((prev) => ({
                ...prev,
                [`service_${index}_${field}`]: !isValid,
            }));
        }
    };

    const handleExclVATBlur = (index) => {
        const currentValue = formData.services[index].exclVAT;
        if (currentValue && !isNaN(parseFloat(currentValue))) {
            const formattedValue = parseFloat(currentValue).toFixed(2);
            const updatedServices = [...formData.services];
            updatedServices[index].exclVAT = formattedValue;
            setFormData({ ...formData, services: updatedServices });
        }
    };

    const handleTemplateChange = (e) => {
        const templateLabel = e;
        setTemplateSelected(true);
        const selectedTemplateData = tempData.find((template) => template.label === templateLabel);
        if (selectedTemplateData) {
            const itemsDescription = selectedTemplateData.items;
            const generatedServices = itemsDescription.map((description) => ({
                numberOfItem: 1,
                description,
                exclVAT: "",
            }));
            setFormData({ ...formData, selectedTemplate: templateLabel, services: generatedServices });
        } else {
            setFormData({ ...formData, selectedTemplate: templateLabel });
        }
    };

    const handleRemoveService = () => {
        setFormData({ ...formData, services: formData.services.slice(0, -1) });
    };

    const handleAddMore = () => {
        setFormData({ ...formData, services: [...formData.services, {}] });
    };

    const handleRemove = (index) => {
        setFormData({ ...formData, services: formData.services.filter((_, i) => i !== index) });
    };

    const onDragEnd = (result) => {
        if (!result.destination) return;
        const items = reorder(formData.services, result.source.index, result.destination.index);
        setFormData({ ...formData, services: items });
    };

    const reorder = (list, startIndex, endIndex) => {
        const result = Array.from(list);
        const [removed] = result.splice(startIndex, 1);
        result.splice(endIndex, 0, removed);
        return result;
    };

    const getItemStyle = (isDragging, draggableStyle) => ({
        userSelect: "none",
        padding: "",
        margin: "0 0 0 0",
        background: isDragging ? "gray" : "white",
        ...draggableStyle,
    });

    const getListStyle = (isDraggingOver) => ({
        background: isDraggingOver ? "lightblue" : "white",
        padding: "4px",
        width: "100%",
    });

    const handleAddItem = () => {
        setPopupFormData({
            ...popupFormData,
            items: [...popupFormData.items, {
                itemName: "",
                quantity: "",
                itemValue: "",
                countryOrigin: "",
                itemHSCode: "",
                hsCodeSource: "database", // Add this new field
            }],
        });
    };

    const handleRemoveItem = (index) => {
        if (popupFormData.items.length > 1) {
            setPopupFormData({
                ...popupFormData,
                items: popupFormData.items.filter((_, i) => i !== index),
            });
        }
    };

    const handlePopupInputChange = (index, field, valueOrEvent) => {
        let value;
        if (typeof valueOrEvent === "object" && valueOrEvent.target) {
            value = valueOrEvent.target.value;
        } else {
            value = valueOrEvent;
        }
        const updatedItems = [...popupFormData.items];
        updatedItems[index][field] = value;
        setPopupFormData({ ...popupFormData, items: updatedItems });
    };

    const loadHSCodeOptions = useCallback(async (inputValue) => {
        if (inputValue.length < 6 || !formData.unloadCountry) {
            return [];
        }
        try {
            const response = await api.get(`/hscodes?country=${formData.unloadCountry}&prefix=${inputValue.slice(0, 6)}`);
            return response.data.map(item => ({
                value: item.HS,
                label: `${item.HS} - ${item.HS4Desc} - ${item.HS6Desc}`
            }));
        } catch (error) {
            console.error('Error fetching HS codes:', error);
            return [];
        }
    }, [formData.unloadCountry]);

    const needsStateSelection = (country) => {
        const { getAlpha2Code } = require("../../../lib/helper/countryToAphlaCode");
        const countryCode = getAlpha2Code(country);
        return countryCode === 'CA' || countryCode === 'BR';
    };

    const getAvailableStates = (country) => {
        const { getAlpha2Code } = require("../../../lib/helper/countryToAphlaCode");
        const countryCode = getAlpha2Code(country);
        if (countryCode === 'CA') {
            return CANADIAN_PROVINCES.map(province => ({
                value: province.state,
                label: province.state
            }));
        }
        if (countryCode === 'BR') {
            return BRAZILIAN_STATES.map(state => ({
                value: state.state,
                label: state.state
            }));
        }
        return [];
    };

    const renderEccnOption = (eccn) => (
        <div className="flex flex-col py-2 px-3 hover:bg-blue-50 transition-colors">
            <div className="flex justify-between items-center">
                <span className="font-semibold text-blue-600">{eccn.eccn_code}</span>
                <span className={`text-xs px-2 py-1 rounded-full ${eccn.restrictions ? 'bg-red-100 text-red-600' : 'bg-green-100 text-green-600'}`}>
                    {eccn.restrictions ? 'Restricted' : 'Unrestricted'}
                </span>
            </div>
            <span className="text-sm text-gray-600 mt-1">{eccn.description}</span>
        </div>
    );

    // Modified handleCalculate wrapper to update Cargo Value after calculation
    const handleCalculateWithCargoUpdate = async () => {
        try {
            // Call the original handleCalculate function
            await handleCalculate({
                formData,
                setFormData,
                popupFormData,
                setPopupFormData,
                adminTemplate,
                eccnCodes,
                disabledCountries,
                disabledDutyVatCountries,
                totalItemsValue,
                totalDimensionsWeight,
                totalDimensionsLength,
                totalDimensionsWidth,
                totalDimensionsHeight,
                totalItemsQuantity,
                itemsTotalQty,
                setIsPopupOpen,
                setAirfreight,
                setInsurance,
                setCif,
                setDuty,
                setCollectionValue,
                setDhlEstimation,
                setPopupValidationErrors,
                setIsCalculating,
                accessToken,
                setAccessToken,
                tokenExpiry,
                setTokenExpiry
            });

            // Update Cargo Value with totalItemsValue after successful calculation
            if (totalItemsValue > 0) {
                setFormData(prevData => ({
                    ...prevData,
                    cargoValue: {
                        ...prevData.cargoValue,
                        value: totalItemsValue.toString()
                    }
                }));
            }
        } catch (error) {
            console.error("Error in calculate estimate:", error);
        }
    };

    // console.log("Form Data: ", formData);

    return (
        <div className="pb-5">
            <div className="w-full flex flex-col gap-5 py-3">
                <h2 className="mx-[30px] font-bold text-xl mt-4">Select Template</h2>
                <div className="flex flex-row items-end gap-4 mx-[30px]">
                    <div className="flex flex-col">
                        <label htmlFor="selectedTemplate" className="mb-1">Select Template</label>
                        <Select
                            name="selectedTemplate"
                            id="selectedTemplate"
                            placeholder="Select Template"
                            onChange={handleTemplateChange}
                            value={formData.selectedTemplate}
                            options={tempData.map((temp) => ({
                                label: temp.label,
                                value: temp.label,
                            }))}
                            showSearch
                        />
                    </div>
                    <div className="flex flex-col">
                        <label htmlFor="currency" className="mb-1">Currency</label>
                        <Select
                            name="currency"
                            id="currency"
                            onChange={(e) => setFormData((prev) => ({ ...prev, currency: e }))}
                            value={formData.currency}
                            options={[
                                { value: "USD", label: "USD" },
                                { value: "EUR", label: "EUR" },
                                { value: "GBP", label: "GBP" },
                            ]}
                            showSearch
                        />
                    </div>
                    {/* Calculate Estimate button disabled and hidden as requested */}
                    {false && formData.selectedTemplate && areMandatoryFieldsFilled() && areCurrenciesAligned() && (
                        <button
                            disabled
                            style={{ display: "none" }}
                            aria-hidden="true"
                            className="hidden btnBg text-white p-2 rounded-md text-sm flex items-center justify-center gap-2 opacity-50 cursor-not-allowed"
                        >
                            Calculate Estimate
                        </button>
                    )}
                </div>
            </div>

            <EstimatePopup
                isOpen={isPopupOpen}
                onClose={() => setIsPopupOpen(false)}
                onCalculate={handleCalculateWithCargoUpdate}
                formData={formData}
                setFormData={setFormData}
                eccnCodes={eccnCodes}
                disabledCountries={disabledCountries}
                popupFormData={popupFormData}
                setPopupFormData={setPopupFormData}
                popupValidationErrors={popupValidationErrors}
                setPopupValidationErrors={setPopupValidationErrors}
                isCalculating={isCalculating}
                handleAddItem={handleAddItem}
                handleRemoveItem={handleRemoveItem}
                handlePopupInputChange={handlePopupInputChange}
                loadHSCodeOptions={loadHSCodeOptions}
                needsStateSelection={needsStateSelection}
                getAvailableStates={getAvailableStates}
                renderEccnOption={renderEccnOption}
            />

            <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="droppable">
                    {(provided, snapshot) => (
                        <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            style={getListStyle(snapshot.isDraggingOver)}
                        >
                            {formData.services.map((formItem, index) => (
                                <Draggable
                                    key={index.toString()}
                                    draggableId={index.toString()}
                                    index={index}
                                >
                                    {(provided, snapshot) => (
                                        <div
                                            ref={provided.innerRef}
                                            {...provided.draggableProps}
                                            {...provided.dragHandleProps}
                                            style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)}
                                        >
                                            <div className="flex flex-col items-center gap-3 w-full md:px-7">
                                                <div className="flex flex-col lg:flex-row gap-3 mx-[30px] w-full" key={index}>
                                                    <div className="w-full lg:w-[20%] flex flex-col gap-1 py-2 relative">
                                                        <label className="w-full text-sm font-normal" htmlFor={`numberOfItem-${index}`}>
                                                            Number
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={`w-full h-8 p-2 text-sm border rounded-md outline-none ${getValidationErrorClass(index, "numberOfItem")}`}
                                                            id={`numberOfItem-${index}`}
                                                            onChange={(e) => handleServiceInputChange(index, "numberOfItem", e)}
                                                            value={formItem.numberOfItem}
                                                        />
                                                    </div>
                                                    <div className="w-full lg:w-[50%] flex flex-col gap-1 py-2">
                                                        <label className="w-full text-sm font-normal" htmlFor={`description-${index}`}>
                                                            Description
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className={`w-full h-8 p-2 text-sm border rounded-md outline-none ${getValidationErrorClass(index, "description")}`}
                                                            id={`description-${index}`}
                                                            onChange={(e) => handleServiceInputChange(index, "description", e)}
                                                            value={formItem.description}
                                                            maxLength={1000}
                                                        />
                                                    </div>
                                                    <div className="w-full lg:w-[10%] flex flex-col gap-1 py-2">
                                                        <label className="w-full text-[gray] text-sm font-normal" htmlFor={`exclVAT-${index}`}>
                                                            excl.VAT
                                                        </label>
                                                        <input
                                                            type="text"
                                                            className="w-full h-8 p-2 text-sm border rounded-md outline-none"
                                                            id={`exclVAT-${index}`}
                                                            onChange={(e) => handleServiceInputChange(index, "exclVAT", e)}
                                                            onBlur={() => handleExclVATBlur(index)}
                                                            value={formItem.exclVAT}
                                                        />
                                                    </div>
                                                    <div className="lg:w-[20%] flex gap-2">
                                                        <div className="w-32">
                                                            <h1 className="mt-2">Total</h1>
                                                            <p className="text-[gray] w-full p-1 text-sm border rounded-md">
                                                                {(parseFloat(formItem.exclVAT || 0) * parseFloat(formItem.numberOfItem || 1)).toFixed(2)}
                                                            </p>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </Draggable>
                            ))}
                            {provided.placeholder}
                        </div>
                    )}
                </Droppable>
                <div className="flex gap-2 items-center">
                    <div className="mx-[30px] mt-3" onClick={handleAddMore}>
                        <FaPlus className="text-[20px] text-[#03336E]" />
                    </div>
                    <div className="mx-[10px] mt-3" onClick={handleRemoveService}>
                        <FaMinus className="text-[20px] text-[#03336E]" />
                    </div>
                </div>
            </DragDropContext>
        </div>
    );
};

export default ServiceTemp;